import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { useTranslation } from "@redotech/redo-i18n";
import { RedoMerchantEmailTemplatesRpcClientContext } from "@redotech/redo-merchant-app-common/email-templates-rpc-client-provider";
import { SendEmailState } from "@redotech/redo-return-core-common/return-flow-step-valid";
import * as selectCss from "@redotech/redo-return-ui/flows/return-flow/select.module.css";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import { Flex } from "@redotech/redo-web/flex";
import { Link } from "@redotech/redo-web/link";
import { SelectDropdown } from "@redotech/redo-web/select-dropdown";
import { Spinner, SpinnerStyle } from "@redotech/redo-web/spinner";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { getTemplateTypeLabel } from "./util";

export const SendEmailDetails = memo(function SendEmailDetails({
  state,
  stepSelect,
  setState,
}: StepTypeDetailsProps<SendEmailState>) {
  const { t } = useTranslation();
  const marketingRpcClient = useRequiredContext(
    RedoMerchantEmailTemplatesRpcClientContext,
  );
  const templatesLoad = useLoad(
    async (signal) => {
      const templates = await marketingRpcClient.getEmailTemplates(
        {},
        { signal },
      );
      return templates;
    },
    [marketingRpcClient],
  );
  const nextStepChange = useHandler((next: StepId | undefined) => {
    setState((state) => ({ ...state, next }));
  });

  return (
    <>
      <Flex align="center" gap="xs">
        <Text fontSize="xs" textColor="secondary">
          {t("Email template -")}
        </Text>
        <Link to="../../email-templates">{t("Create new")}</Link>
      </Flex>
      {templatesLoad.value ? (
        <SelectDropdown
          disabled={!templatesLoad.value}
          options={
            templatesLoad.value?.filter(
              (t) => t.category === "Order tracking",
            ) || []
          }
          value={templatesLoad.value?.find(
            (template: any) => template._id === state.emailId,
          )}
          valueChange={(e: any) =>
            setState((state) => ({
              ...state,
              emailId: e._id,
              emailName: e.name,
              emailType: e.templateType,
            }))
          }
        >
          {(type) => (
            <div className={selectCss.value}>
              <div className={selectCss.title}>
                {type.templateType
                  ? t("{{templateName}} - {{templateType}}", {
                      templateName: type.name,
                      templateType: getTemplateTypeLabel(t, type.templateType),
                    })
                  : type.name}
              </div>
            </div>
          )}
        </SelectDropdown>
      ) : (
        <div style={{ width: "21px", marginLeft: "auto", marginRight: "auto" }}>
          <Spinner style={SpinnerStyle.AUTO} />
        </div>
      )}
      <Text fontSize="xs" textColor="secondary">
        {t("Next step")}
      </Text>
      {stepSelect({ value: state.next, valueChange: nextStepChange })}
    </>
  );
});
